import * as React from 'react';
import { cn } from '@/lib/utils';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { premiumTheme } from './tokens';

/**
 * AlertCard \u2014 Premium recommendation/alert card (replacement for heavy
 * red/yellow blocks). Uses soft intent background + strong on-soft text +
 * hairline border matching the intent.
 */

type Variant = 'info' | 'warning' | 'error' | 'success';

const VARIANT_ICON: Record<Variant, React.ElementType> = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
  success: CheckCircle2,
};

const VARIANT_STYLES: Record<Variant, { bg: string; border: string; text: string }> = {
  info:    { bg: 'bg-info-soft',        border: 'border-info/25',        text: 'text-info-strong'        },
  warning: { bg: 'bg-warning-soft',     border: 'border-warning/30',     text: 'text-warning-strong'     },
  error:   { bg: 'bg-destructive-soft', border: 'border-destructive/30', text: 'text-destructive-strong' },
  success: { bg: 'bg-success-soft',     border: 'border-success/25',     text: 'text-success-strong'     },
};

export interface AlertCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'onDismiss'> {
  variant?: Variant;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Override the default icon (pass `null` to hide) */
  icon?: React.ReactNode | null;
  /** Primary action button/link */
  action?: React.ReactNode;
  /** Dismiss handler; shows an "\u00d7" button when provided */
  onDismiss?: () => void;
}

export const AlertCard = React.forwardRef<HTMLDivElement, AlertCardProps>(
  (
    {
      variant = 'info',
      title,
      description,
      icon,
      action,
      onDismiss,
      className,
      ...props
    },
    ref,
  ) => {
    const styles = VARIANT_STYLES[variant];
    const DefaultIcon = VARIANT_ICON[variant];
    const resolvedIcon =
      icon === null ? null : icon ?? <DefaultIcon aria-hidden="true" />;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'flex items-start gap-3 rounded-xl border p-3 md:p-4',
          styles.bg,
          styles.border,
          className,
        )}
        {...props}
      >
        {resolvedIcon && (
          <div
            className={cn(
              'flex-shrink-0 mt-0.5 [&>svg]:h-4 [&>svg]:w-4',
              styles.text,
            )}
          >
            {resolvedIcon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div
            className={cn(
              'text-[13px] font-semibold leading-tight',
              styles.text,
            )}
          >
            {title}
          </div>
          {description && (
            <div
              className={cn(
                premiumTheme.typography.body,
                'mt-1',
                styles.text,
                'opacity-90',
              )}
            >
              {description}
            </div>
          )}
          {action && <div className="mt-2 flex gap-2">{action}</div>}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className={cn(
              'flex-shrink-0 p-1 rounded-md hover:bg-black/5 dark:hover:bg-card/10 transition-colors',
              styles.text,
            )}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M3 3L9 9M9 3L3 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    );
  },
);
AlertCard.displayName = 'AlertCard';
